import React, { FC, RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal, findDOMNode } from "react-dom";
import cx from "classnames";

import { updateListPosition } from "./../../../helpers/updateListPosition";
import { isLogicOption } from "./../../../helpers/isLogicOption";
import { getScrollParent } from "./../../../helpers/getScrollParent";
import { ILogicOptions, IOtherLogicOptions } from "..";

import styles from "./styles.module.scss";

interface IProps {
    onChange: (newKey: string, index: number) => void;
    setIsOpen: (newValue: boolean) => void;
    options: ILogicOptions | IOtherLogicOptions;
    selectField: RefObject<HTMLButtonElement>;
    currentKey?: string | null;
    hoverElementIndex: number;
    isLogicTimezone?: boolean;
}

const List: FC<IProps> = ({ onChange, setIsOpen, options, selectField, currentKey, hoverElementIndex }) => {
    const listElement = useRef<HTMLButtonElement>(null);

    useLayoutEffect(() => {
        const scrollParent = getScrollParent(selectField.current);

        const onUpdatePosition = () => {
            updateListPosition(listElement, selectField, setIsOpen, scrollParent);
        };

        onUpdatePosition();
        if (scrollParent) scrollParent.addEventListener("scroll", onUpdatePosition);
        window.addEventListener("resize", onUpdatePosition);

        return () => {
            window.removeEventListener("resize", onUpdatePosition);
            if (scrollParent) scrollParent.addEventListener("scroll", onUpdatePosition);
        };
    }, [listElement, selectField, setIsOpen]);

    useEffect(() => {
        function handleClickOutside(event: Event) {
            const target: any = event && event.target;
            if (
                selectField.current &&
                !selectField.current.contains(target) &&
                listElement.current &&
                !findDOMNode(listElement.current)?.contains(target)
            )
                setIsOpen(false);
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("focusout", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("focusout", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectField, listElement]);

    useEffect(() => {
        if (listElement.current) {
            const listItem = listElement.current.children[hoverElementIndex];

            if (listItem) {
                const { y: listItemTop } = listItem.getBoundingClientRect();
                const { y: listTop } = listElement.current.getBoundingClientRect();

                listElement.current.scrollTo({ top: listItemTop + listElement.current.scrollTop - listTop });
            }
        }
    }, [hoverElementIndex]);

    const list = (
        <button ref={listElement} className={cx(styles.list)} onBlur={() => setIsOpen(false)}>
            {Object.keys(options).map((_key: string, index: number) => (
                <React.Fragment key={`${_key}-${Math.random()}`}>
                    <div
                        className={cx(styles.item, {
                            [styles.active]: currentKey === _key,
                            [styles.hover]: hoverElementIndex === index,
                        })}
                        onClick={() => onChange(_key, index)}
                    >
                        {isLogicOption(options[_key])
                            ? (options[_key] as { label: string; type: string }).label
                            : options[_key]}
                    </div>
                </React.Fragment>
            ))}
        </button>
    );

    const root = document.getElementById("root");

    return <>{root ? createPortal(list, root) : list}</>;
};

export default List;
